import type { MediaEntry, MediaRef } from "@/types";
import { assetUrl, imageSize, imageUrl, type MediaContext } from "./mediaUrl";

/**
 * Turning stored media references into what the gallery renders.
 *
 * `react-image-gallery` takes `{ original, thumbnail }` per slide, so this is the
 * adapter between our `MediaRef`/`MediaEntry` shapes and that contract. Ported
 * from the legacy `_utils/mediaUtils`, with the URL building delegated to
 * `mediaUrl` instead of repeated inline.
 */

/** One slide. `embedUrl` is set for anything shown in an iframe or a PDF viewer. */
export interface MediaInfo {
  original: string;
  thumbnail: string;
  originalTitle?: string;
  embedUrl?: string;
}

/** Full-size slides fit the viewport whole; thumbnails are cropped squares. */
const FULL_SIZE = imageSize(1024, 1024, "contain");
const THUMB_SIZE = imageSize(256, 256);

/** Stand-ins for media that has no image of its own. */
export const PLACEHOLDER = "/images/pattern.png";
export const VIDEO_PLACEHOLDER = "/images/video_placeholder.jpg";
export const PDF_PLACEHOLDER = "/images/pdf_placeholder.png";

/** A stored image as a gallery slide, or null when it resolves to no URL. */
export function toMediaInfo(
  ref: MediaRef | undefined | null,
  media: MediaContext
): MediaInfo | null {
  const original = imageUrl(ref ?? undefined, FULL_SIZE, media);
  if (!original) return null;

  return {
    original,
    thumbnail: imageUrl(ref ?? undefined, THUMB_SIZE, media) ?? original,
    originalTitle: ref?.fileName,
  };
}

/**
 * A media entry that opens something external — a video, a 3D tour, a web page.
 *
 * The entry's own image is the slide when it has one; otherwise the caller's
 * placeholder stands in, so the gallery button still has something to show.
 */
export function toExternalMediaInfo(
  entry: MediaEntry,
  placeholder: string,
  media: MediaContext
): MediaInfo {
  const info = toMediaInfo(entry.image, media);

  return {
    original: info?.original ?? placeholder,
    thumbnail: info?.thumbnail ?? placeholder,
    originalTitle: info?.originalTitle,
    embedUrl: entry.url ?? "",
  };
}

const YOUTUBE =
  /^.*((youtu\.be\/)|(youtube\.com\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
const VIMEO =
  /(?:www\.|player\.)?vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^/]*)\/videos\/|album\/(?:\d+)\/video\/|video\/|)(\d+)(?:[a-zA-Z0-9_-]+)?/i;

/** The 11-character YouTube video id in a watch/share/embed URL, if there is one. */
export function getYoutubeId(url: string | undefined): string | null {
  const match = url?.match(YOUTUBE);
  if (!match) return null;

  // The id lands in one group or the other depending on which URL form matched;
  // its fixed length is what tells a real id from a stray path segment.
  return [match[8], match[7]].find((part) => part?.length === 11) ?? null;
}

/** The numeric Vimeo video id in any of its URL forms. */
export function getVimeoId(url: string | undefined): string | null {
  return url?.match(VIMEO)?.[1] ?? null;
}

/**
 * A video entry as a gallery slide.
 *
 * YouTube and Vimeo are recognised so the slide plays inline in their embed
 * player rather than sending the user away, and YouTube additionally provides a
 * poster frame for a video with no image of its own — Vimeo has no equivalent URL
 * without an API call, so it falls back to the shipped placeholder.
 */
export function toVideoMediaInfo(
  entry: MediaEntry,
  media: MediaContext
): MediaInfo {
  const info = toMediaInfo(entry.image, media);

  const youtubeId = getYoutubeId(entry.url);
  if (youtubeId) {
    const poster = `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
    return {
      original: info?.original ?? poster,
      thumbnail: info?.thumbnail ?? poster,
      originalTitle: info?.originalTitle,
      embedUrl: `https://www.youtube.com/embed/${youtubeId}?rel=0&showinfo=0`,
    };
  }

  const vimeoId = getVimeoId(entry.url);
  const embedUrl = vimeoId
    ? `https://player.vimeo.com/video/${vimeoId}?title=0&byline=0&portrait=0`
    : (entry.url ?? "");

  return {
    original: info?.original ?? VIDEO_PLACEHOLDER,
    thumbnail: info?.thumbnail ?? VIDEO_PLACEHOLDER,
    originalTitle: info?.originalTitle,
    embedUrl,
  };
}

/**
 * A mirrored PDF as a gallery slide.
 *
 * The document itself is served verbatim through the asset route, from the path
 * the sync stored. Legacy instead assembled `asset/data/pdfs/{fileName}` by hand,
 * which only resolved for tenants whose files happened to sit in that folder.
 */
export function toPdfMediaInfo(
  ref: MediaRef | undefined,
  media: MediaContext
): MediaInfo | null {
  const embedUrl = assetUrl(ref, media);
  if (!embedUrl) return null;

  return {
    original: PDF_PLACEHOLDER,
    thumbnail: PDF_PLACEHOLDER,
    originalTitle: ref?.fileName,
    embedUrl,
  };
}
